import { PortalClient } from '@pinnakl/investor-portal/domain';
import { DataRoomDocument, FolderDocument } from '@pinnakl/shared/types';
import { MenuItem } from 'primeng/api';
import { VdrPermissionFeatures } from './vdr-permissions.model';

export enum VirtualDataRoomRoutes {
  Documents = 'documents',
  DocumentId = 'documents/:dataroomFolderIdParam',
  Analytics = 'analytics',
  AccessManagement = 'users-management',
}

export enum VdrFolderActions {
  SetActive = 'selectFolder',
  Add = 'addFolder',
  Edit = 'editFolder',
  AddToFavorite = 'addToFavorite',
  DropNone = 'dropNone',
  MoveDocumentToFolder = 'moveDocumentToFolder',
  Remove = 'removeFolder',
  Share = 'shareFolder',
  Notify = 'notify',
  DownloadFile = 'downloadFile',
  DownloadFolder = 'downloadFolder',
  Upload = 'upload',
  UpdateAllFolders = 'UpdateAllFolders',
  Copy = 'Copy',
  Pin = 'Pin',
  Analytics = 'Analytics',
  ToggleWatermark = 'ToggleWatermark',
}

export enum VdrDocumentActions {
  Copy = 'Copy',
  Download = 'Download',
  ChangeParentFolder = 'ChangeParentFolder',
  ToggleWatermark = 'ToggleWatermark',
  ToggleDownloadAllowed = 'ToggleDownloadAllowed',
  Delete = 'Delete',
  Edit = 'Edit',
  SeeAnalytics = 'SeeAnalytics',
  DocumentSelected = 'DocumentSelected',
  DocumentUnselected = 'DocumentUnselected',
}

export interface VdrFolderActionEvent<T, AD> {
  type: VdrFolderActions;
  data: T | boolean;
  files?: File[];
  additionalData?: AD;
}

export interface VdrDocumentActionEvent {
  type: VdrDocumentActions;
  data: DataRoomDocument;
  files?: File[];
  selectedDocuments?: DataRoomDocument[];
}

interface ItemActionButton<T> {
  label: string;
  icon?: string;
  action: T;
  feature?: VdrPermissionFeatures;
  showInDocumentsCloud?: boolean;
  disabled?: (document: FolderDocument, client: PortalClient | null) => boolean;
  visibility?: (document: FolderDocument, client: PortalClient | null) => boolean;
  getIcon?: (document: FolderDocument) => string;
}

export type VdrFolderItemActionButton = ItemActionButton<VdrFolderActions>;
export type VdrDocumentItemActionButton = ItemActionButton<VdrDocumentActions>;

export const VDR_TABS: MenuItem[] = [
  {
    label: 'Analytics',
    portalHidden: true,
    routerLink: VirtualDataRoomRoutes.Analytics,
    routerLinkActiveOptions: { exact: true },
  },
  {
    label: 'Documents',
    routerLink: VirtualDataRoomRoutes.Documents,
    routerLinkActiveOptions: { exact: false },
  },
  {
    label: 'Access Management',
    portalHidden: true,
    routerLink: VirtualDataRoomRoutes.AccessManagement,
    routerLinkActiveOptions: { exact: true },
  },
];

export interface AccessItem {
  id: number;
  name: string;
}

export interface VdrUserModelResponse {
  id: number;
  clientId: number;
  clientPrefix: string;
  portalUserId: number;
  action: null;
  email: string;
  phoneNumber: string | null;
  authType: string;
  otpChannel: string;
  accessDate: string;
  userStatus: string;
  contactId: number;
  folders: AccessItem[];
  groups: AccessItem[];
  credentialsSentDate: string;
  passwordResetRequired: boolean;
  firstName: string;
  lastName: string;
  clientPublicId: string;
  clientName: string;
  organizations: {
    id: number;
    name: string;
  }[];
  active: boolean;
  sendFileNotification: boolean;
  canAccessFundPerformance: boolean;
  canDownloadFiles: boolean;
  canUploadFiles: boolean;
}

export enum VdrUserStatus {
  Active = 'Active',
  Disabled = 'Disabled',
  New = 'New',
}

export enum VdrUserAction {
  Edit = 'edit',
  Access = 'access',
  Deactivate = 'deactivate',
  Delete = 'delete',
  Analytics = 'analytics',
}

export interface VdrUserModel extends VdrUserModelResponse {
  fullName: string;
  foldersArray: string[];
  status: VdrUserStatus;
}

export interface AnalyticItem {
  activityType: string;
  resourceName: null | string;
  timestamp: string; // date time str
  user: null | {
    name: string;
    organizations: string[];
    email: string;
  };
}

export interface ChangeWatermarkPayload {
  id: number;
  iswatermarked: 'true' | 'false';
  watermarkTemplateId?: number | null;
}

export interface UploadFileDetails {
  withWatermark: boolean;
  watermarkTemplateId?: number | null;
  linkDetails?: {
    description: string;
    points: number;
  };
  effectiveDate?: Date | null;
  downloadAllowed?: boolean;
}

export const VDR_SIDEBAR_MIN_WIDTH = 200; // 12.5rem
export const VDR_SIDEBAR_DEFAULT_WIDTH = 320; // 20rem
export const VDR_SIDEBAR_MAX_WIDTH = 576; // 36rem
export const VDR_COLLAPSED_SIDEBAR_WIDTH = 48; // 3rem
export const VDR_SIDEBAR_WIDTH_STORAGE_KEY = 'vdrSidebarWidth';
export const VDR_SIDEBAR_EXPANDED_STORAGE_KEY = 'vdrSidebarExpanded';
export const VDR_SIDEBAR_SELECTOR = '.documents-sidebar';
